import classnames from 'classnames';
import React, { useRef, useState, useCallback, useEffect } from 'react';
import { debounce } from 'lodash-es';
import { useRequest, useDeepCompareEffect } from 'ahooks';
import { Dropdown, SearchBox, StatusTip, Text } from 'tea-component';
import SvgIcon from '@home/components/SvgIcon';
import EllipsisTextBubble from '@gp/components/EllipsisTextBubble';
import PrejectsItems from '@gp/pages/Pipeline/components/ProjectsItems';
import useClickoutside from '@gp/hooks/useClickoutside';
import { sortByProName } from '@gp/utils/project';
import { reportOperationLog } from '@gp/services/global';
import { isIncludeConsole } from '@gp/utils/common';
import {
  getSearchList, // all peojects列表模糊搜索
  queryRecord, // 查询搜索历史
  pushRecord, // 添加搜索历史
  deleteRecord, // 删除搜索历史
} from '@gp/services/project';
import { getWorkbenchList } from '@gp/services/gateReview'; // 工作台模糊搜索
import { fetchStudioOverallOverviewTableData } from '@gp/pages/StudioDashboard/StudioOverall/services/studioOverall'; // studio overall - overview列表模糊搜索
import { HISTORY_PAGE_TYPES } from '@gp/constants/const';
import { SEARCH_SELECT_COMP_AUTO_CLOSE_WHEN_SCROLL_NODE } from '@gp/constants/classNames';
import { ALL_REPORT_OPERATION_LOG } from '@gp/constants/operationLogParams';
import '@gp/assets/css/pages/pipelineProjectlist.scss';
import './index.scss';

const { LoadingTip, EmptyTip } = StatusTip;

// 搜索历史对应传的type
const HISTORY_PAGE_TYPE = {
  projectList: HISTORY_PAGE_TYPES.PROJECT_LIST,
  workbench: HISTORY_PAGE_TYPES.WORKBENCH,
  studioOverview: HISTORY_PAGE_TYPES.STUDIO_OVERVIEW,
  pipelineGRActionItems: HISTORY_PAGE_TYPES.PIPELINE_GR_ACTION_ITEMS,
  pipelineChangeLogChangeDetail:
    HISTORY_PAGE_TYPES.PIPELINE_CHANGE_LOG_CHANGE_DETAIL,
  pipelineChangeLogChangeChecklist:
    HISTORY_PAGE_TYPES.PIPELINE_CHANGE_LOG_CHANGE_CHECKLIST,
  overview: HISTORY_PAGE_TYPES.OVERVIEW,
} as any;

// 搜索历史对应的pageId，用于打点
const PAGE_IDS = {
  projectList: ALL_REPORT_OPERATION_LOG.PIPELINE_PROJECT_LIST.pageId,
  pipelineGRActionItems:
    ALL_REPORT_OPERATION_LOG.PIPELINE_GR_ACTION_ITEMS.pageId,
  pipelineChangeLogChangeDetail:
    ALL_REPORT_OPERATION_LOG.PIPELINE_CHANGE_LOG_CHANGE_DETAIL.pageId,
  pipelineChangeLogChangeChecklist:
    ALL_REPORT_OPERATION_LOG.PIPELINE_CHANGE_LOG_CHANGE_CHECKLIST.pageId,
  overview: ALL_REPORT_OPERATION_LOG.PIPELINE_DASHBOARD.pageId,
} as any;

const ACTIVED_CLASS_NAME = 'dbg-recent-search-dropdown-modal-items-actived';

const NEEDLESS_NUM = 0;

const SearchCommon: React.FC<any> = (props: any) => {
  const {
    domain,
    currentPage,
    isTodo = true,
    hasSearchBtn = true,
    submit,
    editBtnDisabled = false,
    showRightBtn = false,
    rightBtnText = 'Details',
    onRightBtnClick,
    placeholder,
    style,
    formatSearchResults,
    updateDeps,
    searchList: searchListProps = [],
    searchVal: searchValProps = '',
    setSearchList: setSearchListProps,
    setSearchVal: setSearchValProps,
    getSearchList: getSearchListProps,
    getDomainId = (record: any) => record?.project_id || '',
    isFixedSearch = false,
  } = props;

  const root = useRef(null);
  const [reportOperationLogMsg, setReportOperationLogMsg] = useState<any>({}); // 打点信息
  const [searchList, setSearchList] = useState(searchListProps); // item数据里面有id为搜索历史记录
  const [searchVal, setSearchVal] = useState(searchValProps);
  const focusIndex = useRef(-1 + NEEDLESS_NUM);
  const timer = useRef(null); // 用于防抖
  const [showDropdown, setShowDropdown] = useState(false);
  const [isShowRecent, setIsShowRecent] = useState(true);
  const [showItemClose, setShowItemClose] = useState('');

  useDeepCompareEffect(() => {
    setSearchVal(searchValProps);
  }, [searchValProps]);

  useDeepCompareEffect(() => {
    setSearchList(searchListProps);
  }, [searchListProps]);

  // 模糊搜索
  const { loading: workbenchLoading, run: runWorkbenchSearch } = useRequest(
    getWorkbenchList,
    {
      manual: true,
      onSuccess: (result) => {
        if (reportOperationLogMsg?.query)
          reportOperationLog(reportOperationLogMsg, 'search');
        setSearchList(result || []);
        setSearchListProps?.(result || []);
      },
    },
  );

  const { loading, run: runSearch } = useRequest(
    getSearchListProps ?? getSearchList,
    {
      manual: true,
      onSuccess: (resultTemp: any) => {
        if (reportOperationLogMsg?.query)
          reportOperationLog(reportOperationLogMsg, 'search');
        let result: any = [];
        switch (currentPage) {
          case 'overview': {
            result = resultTemp?.data || [];
            break;
          }
          default:
            result = resultTemp;
            break;
        }
        const formatList = formatSearchResults?.(result) || result || [];
        const sortList = sortByProName(formatList);
        setIsShowRecent(false);
        setSearchList(sortList); // 存到searchList，且数据里面有id为历史记录list
        setSearchListProps?.(sortList); // 存到searchList，且数据里面有id为历史记录list
      },
    },
  );

  const { loading: overviewLoading, run: runOverviewSearch } = useRequest(
    fetchStudioOverallOverviewTableData,
    {
      manual: true,
      onSuccess: (result: any) => {
        if (reportOperationLogMsg?.query)
          reportOperationLog(reportOperationLogMsg, 'search');
        const searchList = Array.isArray(result?.list)
          ? result?.list?.map((t: any) => {
            const newT = {
              ...t,
              name: t?.studio_name || '',
              img: t?.studio_cover || '',
            };
            return newT;
          })
          : [];
        setIsShowRecent(false);
        setSearchList(searchList);
        setSearchListProps?.(searchList);
      },
    },
  );

  // 查询搜索历史
  const { loading: recordLoading, run: runQueryRecord } = useRequest(
    queryRecord,
    {
      manual: true,
      onSuccess: (result: any = []) => {
        setShowDropdown(true);
        setIsShowRecent(true);
        switch (currentPage) {
          case 'studioOverview': {
            const searchList = Array.isArray(result)
              ? result?.map((t) => ({
                ...t,
                studio_name: t?.name || '',
                studio_id: t?.project_id || '',
              }))
              : [];
            setSearchList(searchList);
            setSearchListProps?.(searchList);
            break;
          }
          default: // 历史记录放入
            setSearchList(result || []);
            setSearchListProps?.(result || []);
            break;
        }
      },
    },
  );
  // 添加搜索历史
  const { run: runPushRecord } = useRequest(pushRecord, {
    manual: true,
    onSuccess: () => {
      if (reportOperationLogMsg?.query)
        reportOperationLog(reportOperationLogMsg, 'search');
    },
  });
  // 删除搜索历史
  const { run: runDeleteRecord } = useRequest(deleteRecord, {
    manual: true,
    onSuccess: () => {
      reportOperationLog(reportOperationLogMsg, 'button');
    },
  });

  // 下拉列表点击的回调
  const onItemClick = (itemData: any, e: any = {}) => {
    if ((e?.target?.ariaLabel ?? '') === 'dismiss') return; // 排除点击的是删除icon
    const { name } = itemData;
    showDropdown && setShowDropdown(false);
    setSearchVal(name);
    setSearchValProps?.(name);
    let params = null;
    let domainId = '';
    switch (currentPage) {
      case 'projectList':
      case 'pipelineGRActionItems':
      case 'pipelineChangeLogChangeDetail':
      case 'pipelineChangeLogChangeChecklist':
        runPushRecord({
          ...itemData,
          project_id: itemData?.project_id,
          type: HISTORY_PAGE_TYPE?.[currentPage],
          // 这个字段在这里无用，只为防止接口报错
          source_type: 0,
        });
        setReportOperationLogMsg({
          query: name,
          extInfo: PAGE_IDS[currentPage], // 当前页面id
          clickId: itemData?.project_id,
          uidType: '',
          uid: '',
        });
        domainId = getDomainId(itemData);
        params = {
          search: [name],
          search_ids: !domainId ? [] : [domainId],
          search_type: 'exact',
          search_keyword: name || '',
        };
        break;
      // case 'workbench':
      //   // workbench列表接口会自动把搜索历史记录，所以这里不用掉runPushRecord
      //   params = {
      //     project_name: name,
      //     project_id: itemData?.project_id,
      //     platform: [],
      //     // platform: itemData?.platform,
      //     is_search: false,
      //     is_todo: isTodo,
      //     // type: reviewType,
      //   };
      //   break;
      case 'studioOverview':
        runPushRecord({
          type: HISTORY_PAGE_TYPE?.[currentPage],
          platform: 'studio',
          project_id: itemData?.studio_id,
        });
        setReportOperationLogMsg({
          query: name,
          extInfo: '500901', // 当前页面id
          clickId: itemData?.studio_id,
          uidType: '',
          uid: '',
        });
        params = itemData;
        break;
      case 'overview':
        runPushRecord?.({
          ...itemData,
          project_id: itemData?.project_id,
          type: HISTORY_PAGE_TYPE?.[currentPage],
          // 这个字段在这里无用，只为防止接口报错
          source_type: 0,
        });
        setReportOperationLogMsg({
          query: name,
          extInfo: PAGE_IDS[currentPage], // 当前页面id
          clickId: itemData?.project_id,
          uidType: '',
          uid: '',
        });
        break;

      default:
        break;
    }
    if (hasSearchBtn === false) {
      onRightBtnClick?.(itemData);
    } else {
      submit?.(params, searchList);
    }
  };

  // 点击搜索按钮或按回车的回调
  const onSearch = (text: any) => {
    if (focusIndex.current > -1 + NEEDLESS_NUM) {
      onItemClick?.(searchList[focusIndex.current - NEEDLESS_NUM]);
    } else if (focusIndex.current <= -1 + NEEDLESS_NUM) {
      // 点击搜索按钮或按回车
      // all projects回车及点搜索时传下拉项所有名称，刚好没有搜索内容时不走接口，避免搜到历史记录中的游戏
      const searchNameList =
        Array?.isArray(searchList) && searchList?.length
          ? searchList
            ?.filter((item) =>
              item?.name?.toLowerCase()?.includes(text?.toLowerCase()),
            )
            ?.map((t) => t?.name?.trim())
          : [text];
      const searchIdList =
        Array?.isArray(searchList) && searchList?.length
          ? searchList
            ?.filter((item) =>
              item?.name?.toLowerCase()?.includes(text?.toLowerCase()),
            )
            ?.map((item) => getDomainId?.(item) || '')
          : [];
      showDropdown && setShowDropdown(false);
      let params = null;
      switch (currentPage) {
        case 'projectList':
        case 'pipelineGRActionItems':
        case 'pipelineChangeLogChangeDetail':
        case 'pipelineChangeLogChangeChecklist':
          params = {
            search: searchNameList,
            search_ids: searchIdList,
            search_type: 'dim',
            search_keyword: text || '',
          };
          break;
        case 'overview':
          if (Array.isArray(searchList) && searchList?.length > 0) {
            onItemClick?.(searchList[0]);
          }
          break;
        case 'workbench':
          params = {
            project_name: text,
            is_search: false,
            is_todo: isTodo,
            // type: reviewType,
          };
          break;

        case 'studioOverview':
          params = {
            studio_name: text,
            studio_id: '',
          };
          break;

        default:
          break;
      }
      if (hasSearchBtn === false) {
        return;
      }
      text && submit?.(params, searchList);
    }
  };

  // 点击删除，去除history
  const deleteRecordClick = useCallback(
    (item: any, e: any) => {
      e.stopPropagation();
      e.preventDefault();
      const filterArr = searchList.filter((el: any) => el.name !== item.name);
      if (filterArr?.length === 0) {
        showDropdown && setShowDropdown(false); // 开的状态的话就关闭
      }
      runDeleteRecord({ id: item.id });
      let params = null;
      switch (currentPage) {
        case 'projectList':
        case 'pipelineGRActionItems':
        case 'pipelineChangeLogChangeDetail':
        case 'pipelineChangeLogChangeChecklist':
        case 'overview':
          params = {
            buttonId: item?.project_id,
            pageId: PAGE_IDS[currentPage],
          };
          break;

        case 'workbench':
          params = {
            buttonId: item?.project_id,
            pageId: '',
          };
          break;
        case 'studioOverview':
          params = {
            buttonId: '500901003', // 解决studioOverview历史删除时，打点报错问题
            extInfo: item?.studio_id,
            pageId: '500901',
          };
          break;

        default:
          break;
      }
      setReportOperationLogMsg(params);
      setSearchList(filterArr);
      setSearchListProps?.(filterArr);
    },
    [searchList, currentPage],
  );

  // 搜索下拉输入框获取焦点的回调
  const onDropdownFocus = () => {
    if (editBtnDisabled) return;
    if (!searchVal && !searchList?.[0]?.id) {
      runQueryRecord({ type: HISTORY_PAGE_TYPE?.[currentPage] }); // 请求搜索记录接口并且用searchList保存
    } else {
      !showDropdown && setShowDropdown(true);
    }
    // if (searchList?.[0]?.id || searchList?.length > 0 || searchVal) {
    //   !showDropdown && setShowDropdown(true);
    // } else if (!searchVal) {
    //   runQueryRecord({ type: HISTORY_PAGE_TYPE?.[currentPage] }); // 请求搜索记录接口并且用searchList保存
    // }
  };

  // 搜索下拉框键盘按下的回调
  const onDropdownKeyDown = (e: any) => {
    if (e.keyCode === 32) return (e.returnValue = false);
    if (!['ArrowUp', 'ArrowDown'].includes(e.key)) return true;
    if (!searchList || searchList?.length === 0) return true;
    // 找到下拉列表的box元素，将box元素中的下一个或上一个子元素添加actived类，使其具有hover样式
    const modalBox = document.getElementsByClassName(
      'dbg-recent-search-dropdown-modal-context',
    );
    if (modalBox?.length) {
      let nextIndex = focusIndex.current;
      const { childNodes } = modalBox[0];
      if (childNodes?.length <= 0 + NEEDLESS_NUM) return true;
      if (e.key === 'ArrowUp') {
        if (focusIndex.current === -1 + NEEDLESS_NUM) return true;
        // eslint-disable-next-line no-plusplus
        nextIndex--;
        if (nextIndex <= -1 + NEEDLESS_NUM) {
          nextIndex = childNodes?.length - 1;
        }
      } else if (e.key === 'ArrowDown') {
        // eslint-disable-next-line no-plusplus
        nextIndex++;
        if (nextIndex >= childNodes?.length) {
          nextIndex = 0 + NEEDLESS_NUM;
        }
      }
      if (focusIndex.current >= 0 + NEEDLESS_NUM) {
        childNodes[focusIndex.current].classList.remove(ACTIVED_CLASS_NAME);
      }
      childNodes[nextIndex].classList.add(ACTIVED_CLASS_NAME);
      focusIndex.current = nextIndex;
    }
    return true;
  };

  // 搜索下拉框失去焦点的回调
  const onDropdownBlur = () => {
    // 删除掉之前添加的actived类
    const modalBox = document.getElementsByClassName(
      'dbg-recent-search-dropdown-modal-context',
    );
    if (modalBox?.length) {
      const activedElement: any =
        modalBox[0]?.childNodes[focusIndex.current] || [];
      if (activedElement) {
        activedElement?.classList?.remove(ACTIVED_CLASS_NAME);
      }
    }
    focusIndex.current = -1 + NEEDLESS_NUM;
  };

  // 判断下拉框是否去关闭
  useClickoutside(root?.current, 'click', () => {
    setShowDropdown?.(false);
  });

  const resetSearchSelect = () => {
    let params = null;
    switch (currentPage) {
      case 'projectList':
      case 'pipelineGRActionItems':
      case 'pipelineChangeLogChangeDetail':
      case 'pipelineChangeLogChangeChecklist':
        params = {};
        break;
      case 'workbench':
        params = {
          sort_field: 'due_date',
          order: 'asc',
          is_search: false,
          is_todo: isTodo,
          // type: reviewType,
        };
        break;
      case 'studioOverview':
        params = {
          studio_name: '',
          studio_id: '',
        };
        break;

      default:
        break;
    }
    if (hasSearchBtn === false) {
      return;
    }
    submit?.(params, searchList);
  };

  const searchOnchange = (val: any) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      // ref实现防抖
      if (val.replace(/^\s+|\s+$/g, '')) {
        // 用正则把输入框空格过滤掉
        const searVal = val.replace(/^(\s*)|(\s*)$/g, ''); // 把用户输入的前后空格过滤掉
        switch (currentPage) {
          case 'projectList':
          case 'pipelineGRActionItems':
          case 'pipelineChangeLogChangeDetail':
          case 'pipelineChangeLogChangeChecklist':
            setReportOperationLogMsg({
              query: searVal,
              extInfo: PAGE_IDS[currentPage], // 当前页面id
              clickId: '',
              uidType: '',
              uid: '',
            });
            runSearch({ projectName: searVal });
            break;
          case 'overview':
            setReportOperationLogMsg({
              query: searVal,
              extInfo: PAGE_IDS[currentPage], // 当前页面id
              clickId: '',
              uidType: '',
              uid: '',
            });
            runSearch({ search: searVal });
            break;
          case 'workbench':
            setReportOperationLogMsg({
              query: searVal,
              extInfo: '', // 当前页面id
              clickId: '',
              uidType: '',
              uid: '',
            });
            runWorkbenchSearch({
              // sort_field: 'due_date',
              // order: 'asc',
              project_name: searVal,
              is_search: true,
              // platform: '',
              is_todo: isTodo,
              // type: reviewType,
            });
            break;

          case 'studioOverview':
            setReportOperationLogMsg({
              query: searVal,
              extInfo: '500901', // 当前页面id
              clickId: '',
              uidType: '',
              uid: '',
            });
            runOverviewSearch({
              studio_name: searVal,
            });
            break;

          default:
            break;
        }
        !showDropdown && setShowDropdown(true);
      } else if (!val && searchVal) {
        // 搜索输入框值为空的情况 ： 请求历史记录，重置list搜索接口请求
        runQueryRecord({ type: HISTORY_PAGE_TYPE?.[currentPage] }); // 请求搜索记录接口并且用searchList保存
        if (isFixedSearch) {
          setShowDropdown(false);
        } else {
          resetSearchSelect(); // 搜索内容为空 //默认展示历史记录
        }
      }
    }, 500);
    setSearchValProps?.(val);
  };

  // 页面发送滚动时关闭弹窗 -- start
  const scrollContainerRef = useRef(null);
  const searchBoxRef = useRef(null);

  const handleScroll = useCallback(() => {
    searchBoxRef.current?.querySelector('.tea-input')?.blur();
    setShowDropdown(false);
  }, []);

  useEffect(() => {
    const t = setInterval(() => {
      const el = document.querySelector(
        `.${SEARCH_SELECT_COMP_AUTO_CLOSE_WHEN_SCROLL_NODE}`,
      );
      // 增加吸顶父容器滑动，关闭搜索下拉框
      const elPa = document.querySelector(`.dbg-page-scroll-body`);
      if (el) {
        scrollContainerRef.current = el;
        el.addEventListener('scroll', handleScroll);
        clearInterval(t);
      }
      if (elPa) {
        scrollContainerRef.current = elPa;
        elPa.addEventListener('scroll', handleScroll);
        clearInterval(t);
      }
    }, 100);

    return () => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);
  // 页面发送滚动时关闭弹窗 -- end

  return (
    <Dropdown
      ref={root}
      className={classnames({
        'dbg-recent-search-dropdown search-select': true,
        'workbench-search-select': currentPage === 'workbench',
      })}
      style={style}
      closeOnScroll={true}
      escapeWithReference={true}
      updateOnChildrenChange={true}
      button={
        <SearchBox
          className={hasSearchBtn ? 'dbg-recent-search-box' : 'drawer-search'}
          style={{ width: 400 }}
          placeholder={placeholder}
          searchBoxRef={searchBoxRef}
          value={searchVal}
          onChange={searchOnchange}
          onSearch={onSearch}
          onPressEnter={onSearch}
          disabled={editBtnDisabled}
        />
      }
      placement="bottom-end"
      appearance="pure"
      updateDeps={updateDeps}
      open={showDropdown}
      matchButtonWidth={true}
      onFocus={onDropdownFocus}
      onKeyDown={onDropdownKeyDown}
      onBlur={onDropdownBlur}
      disabled={editBtnDisabled}
      popupContainer={() => root?.current}
      boxClassName={classnames(
        'dbg-recent-search-dropdown-modal search-body globalpedia-modal-font14',
        {},
      )}
    >
      {loading || workbenchLoading || overviewLoading || recordLoading ? (
        <LoadingTip loadingText="Loading..." className="loading" />
      ) : (
        <div className="dbg-recent-search-dropdown-modal-box">
          {searchList && searchList?.length > 0 ? (
            <>
              {isShowRecent && (
                <Text className="dbg-recent-search-dropdown-modal-title">
                  Recent
                </Text>
              )}
              <div className="dbg-recent-search-dropdown-modal-context">
                {searchList.map((item: any) => {
                  const platformLowerCase =
                    (Array.isArray(item?.platform)
                      ? item?.platform.join(',')?.toLowerCase()
                      : item?.platform?.trim()?.toLowerCase()) || '';
                  return (
                    <div
                      className="dbg-recent-search-dropdown-modal-items"
                      key={item.name}
                      onClick={(e) => {
                        onItemClick(item, e);
                      }}
                      onMouseEnter={() => {
                        setShowItemClose(item.name);
                      }}
                      // 鼠标移出的时候取消删除按钮
                      onMouseLeave={() => {
                        setShowItemClose('');
                      }}
                    >
                      <PrejectsItems
                        domain={domain}
                        content={item.name}
                        cover={item.img}
                        searchKey={searchVal}
                        style={showRightBtn ? { alignItems: 'center' } : null}
                        showRightBtn={showRightBtn}
                        showItemClose={item.id && showItemClose === item.name}
                        rightBtnText={rightBtnText}
                        onRightBtnClick={(isJump = true) => {
                          if (isJump) {
                            // 点击进入详情页添加搜索历史
                            const { name } = item;
                            switch (currentPage) {
                              case 'projectList':
                              case 'pipelineGRActionItems':
                              case 'pipelineChangeLogChangeDetail':
                              case 'pipelineChangeLogChangeChecklist':
                                runPushRecord?.({
                                  ...item,
                                  project_id: item?.project_id,
                                  type: HISTORY_PAGE_TYPE?.[currentPage],
                                  // 这个字段在这里无用，只为防止接口报错
                                  source_type: 0,
                                });
                                setReportOperationLogMsg({
                                  query: name,
                                  extInfo: PAGE_IDS[currentPage], // 当前页面id
                                  clickId: item?.project_id,
                                  uidType: '',
                                  uid: '',
                                });
                                break;

                              case 'studioOverview': {
                                const pushRecord = {
                                  type: HISTORY_PAGE_TYPE?.[currentPage],
                                  platform: 'studio',
                                  project_id: item?.studio_id,
                                } as any;
                                runPushRecord(pushRecord);
                                setReportOperationLogMsg({
                                  query: name,
                                  extInfo: '500901', // 当前页面id
                                  clickId: item?.studio_id,
                                  uidType: '',
                                  uid: '',
                                });
                                break;
                              }
                              default:
                                break;
                            }
                            onRightBtnClick?.(item);
                          }

                          return onRightBtnClick?.(item, false);
                        }}
                        onCloseIcon={debounce(
                          (e) => deleteRecordClick(item, e),
                          100,
                        )}
                      >
                        {currentPage === 'studioOverview' ? (
                          <section className="gp-search-dropdown-modal-studio-overview-slot">
                            <img
                              style={{ marginRight: '4px' }}
                              width={16}
                              height={10}
                              src={item?.national_flag}
                            />
                            <EllipsisTextBubble
                              style={{ color: 'var(--color-neutral-5)' }}
                              text={item?.location_country || '-'}
                            />
                          </section>
                        ) : (
                          <div className="dbg-prejects-slot">
                            {(platformLowerCase?.toLowerCase() ===
                              'all platforms' ||
                              platformLowerCase
                                ?.toLowerCase()
                                .includes('mobile')) && (
                              <SvgIcon type="other-icon" />
                            )}
                            {(platformLowerCase?.toLowerCase() ===
                              'all platforms' ||
                              platformLowerCase
                                ?.toLowerCase()
                                .includes('pc')) && <SvgIcon type="monitor" />}
                            {(platformLowerCase?.toLowerCase() ===
                              'all platforms' ||
                              isIncludeConsole(platformLowerCase)) && (
                              <SvgIcon type="game" />
                            )}
                          </div>
                        )}
                      </PrejectsItems>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <EmptyTip emptyText="No results found" className="empty" />
          )}
        </div>
      )}
    </Dropdown>
  );
};

export default SearchCommon;
